import { InitialState } from 'Container/App/interface';
import {
    ADDITIONAL_PAGE_OPTIONS,
    FLOW_SUBSCRIPTIONS_FIRST,
    RED_XL_VIRTUAL_ID,
    RedTariff,
    SalesChannel,
    SALESCHANNEL_CONSUMER,
    SALESCHANNEL_FAMILY_FRIENDS,
    SALESCHANNEL_YOUNG,
    YoungTariff,
} from '@vfde-sails/constants';
import {
    asyncThunkCreator,
    buildCreateSlice,
    original,
} from '@reduxjs/toolkit';
import { createSelector } from 'reselect';
import { MAXIMUM_RED_PLUS_CARDS } from 'Container/App/constants';
import { toggleArrayItem } from '@vfde-sails/utils';
import getPageUrlForCustomerConfig from 'Container/App/helpers/getPageUrlForCustomerConfig';
import {
    getHashcode,
    startFnFValidation,
} from '@vfde-sails/validation';
import {
    getSimOnly,
    getTariffWithHardware,
} from 'Container/App/api/glados';
import { RootState } from '../../store';
import { RequestError } from '@vfde-sails/types';

const initialState: InitialState = {
    availableSubscriptionIds: [],
    customerConfig: {
        flowType: FLOW_SUBSCRIPTIONS_FIRST,
        salesChannel: window[ADDITIONAL_PAGE_OPTIONS].salesChannel,
        selectedSubscriptionId: null,
        defaultSubscriptionId: null,
        atomicId: null,
        deviceId: null,
        hasGigakombi: !!window[ADDITIONAL_PAGE_OPTIONS].isGigaKombi,
        insurance: null,
        hasTauschbonus: false,
        hasTradeIn: false,
        isRestlaufzeit: false,
        redplusIds: [],
        hasCashback: false,
    },
    previousHardwareConfig: null,
    skipConnectMore: false,
};

/**
 * Create async capable app slice
 */
export const createAppSlice = buildCreateSlice({
    creators: { asyncThunk: asyncThunkCreator },
});

const appSlice = createAppSlice({
    name: 'TariffOverview',
    initialState,
    reducers: create => {
        const createAThunk = create.asyncThunk.withTypes<{
            rejectValue: string;
        }>();

        const createAThunkWithRequestErrorRejectValue = create.asyncThunk.withTypes<{
            rejectValue: RequestError;
        }>();

        return {
            // eslint-disable-next-line max-len
            setInitialState: createAThunk<Pick<InitialState, 'availableSubscriptionIds' | 'customerConfig' | 'previousHardwareConfig'>, Pick<InitialState, 'availableSubscriptionIds' | 'customerConfig' | 'previousHardwareConfig'>>(
                async (arg, thunkAPI) => {
                    const { customerConfig } = arg;

                    if (customerConfig.hasGigakombi !== !!window[ADDITIONAL_PAGE_OPTIONS].isGigaKombi) {
                        const pageUrl = getPageUrlForCustomerConfig(customerConfig.salesChannel!, customerConfig.hasGigakombi);

                        throw thunkAPI.rejectWithValue(pageUrl);
                    }

                    if (customerConfig.salesChannel === SALESCHANNEL_FAMILY_FRIENDS) {
                        const hashcode = getHashcode();

                        const result = await thunkAPI.dispatch(startFnFValidation(hashcode));

                        if (startFnFValidation.rejected.match(result)) {
                            throw thunkAPI.rejectWithValue(result.payload!);
                        }
                    }

                    return {
                        availableSubscriptionIds: arg.availableSubscriptionIds,
                        customerConfig: arg.customerConfig,
                        previousHardwareConfig: arg.previousHardwareConfig,
                    };
                },
                {
                    fulfilled: (state, action) => {
                        state.customerConfig = action.payload.customerConfig;
                        state.availableSubscriptionIds = action.payload.availableSubscriptionIds;
                        state.previousHardwareConfig = action.payload.previousHardwareConfig;
                    },
                },
            ),
            getData: createAThunkWithRequestErrorRejectValue<void, void>(
                async (_, thunkAPI): Promise<any> => {
                    const dispatches = [];

                    const { isRestlaufzeit } = selectCustomerConfig(thunkAPI.getState() as RootState);
                    const isEligibleForRestlaufzeit = selectIsEligibleForRestlaufzeit(thunkAPI.getState() as RootState);

                    dispatches.push(thunkAPI.dispatch(getSimOnly.initiate(isEligibleForRestlaufzeit && isRestlaufzeit)));

                    const atomicId = selectAtomicId(thunkAPI.getState() as RootState);

                    if (atomicId) {
                        dispatches.push(thunkAPI.dispatch(getTariffWithHardware.initiate({
                            isRestlaufzeit: isEligibleForRestlaufzeit && isRestlaufzeit,
                            atomicId,
                        })));
                    }

                    const result = await Promise.all(dispatches);

                    for (const action of result) {
                        if (action.isError) {
                            return thunkAPI.rejectWithValue(action.error as RequestError);
                        }
                    }
                },
            ),
            setSubscription: create.reducer<RedTariff | YoungTariff | null>((state, action) => {
                state.customerConfig.selectedSubscriptionId = action.payload;
            }),
            setGigakombi: create.reducer<boolean>((state, action) => {
                state.customerConfig.hasGigakombi = action.payload;
            }),
            setSalesChannel: create.reducer<Extract<SalesChannel, typeof SALESCHANNEL_CONSUMER | typeof SALESCHANNEL_YOUNG>>(
                (state, action) => {
                    state.customerConfig.salesChannel = action.payload;
                },
            ),
            setRestlaufzeit: create.reducer<boolean>((state, action) => {
                state.customerConfig.isRestlaufzeit = action.payload;
            }),
            setCashback: create.reducer<boolean>((state, action) => {
                state.customerConfig.hasCashback = action.payload;
            }),
            goToBasket: create.reducer<boolean | undefined>((state, action) => {
                if (action.payload !== undefined) {
                    state.customerConfig.hasGigakombi = action.payload;
                }
            }),
            toggleDevice: create.reducer<void>(state => {
                const originalState = original(state) as InitialState;

                state.customerConfig.atomicId = originalState.customerConfig.atomicId
                    ? null
                    : originalState.previousHardwareConfig?.atomicId || null;
                state.customerConfig.deviceId = originalState.customerConfig.deviceId
                    ? null
                    : originalState.previousHardwareConfig?.deviceId || null;
                state.customerConfig.hasTradeIn = originalState.customerConfig.atomicId
                    ? false
                    : originalState.previousHardwareConfig?.hasTradeIn || false;
                state.customerConfig.hasTauschbonus = originalState.customerConfig.atomicId
                    ? false
                    : originalState.previousHardwareConfig?.hasTauschbonus || false;
                state.customerConfig.insurance = originalState.previousHardwareConfig?.insurance || null;
                state.previousHardwareConfig = originalState.customerConfig.atomicId
                    ? {
                        atomicId: originalState.customerConfig.atomicId,
                        insurance: originalState.customerConfig.insurance,
                        deviceId: originalState.customerConfig.deviceId,
                        hasTauschbonus: originalState.customerConfig.hasTauschbonus,
                        hasTradeIn: originalState.customerConfig.hasTradeIn,
                    }
                    : null;
            }),
            toggleRedPlusId: create.preparedReducer(
                (id: string, force?: boolean) => ({
                    payload: {
                        id,
                        force,
                    },
                }),
                (state, action) => {
                    const originalState = original(state) as InitialState;
                    const isToBeAdded = !originalState.customerConfig.redplusIds.some(id => id === action.payload.id);

                    if (action.payload.force !== undefined) {
                        if ((!isToBeAdded && action.payload.force) || (isToBeAdded && !action.payload.force)) {
                            return;
                        }
                    }

                    // Only add max. 4 RedPlus cards.
                    if (!isToBeAdded || state.customerConfig.redplusIds.length < MAXIMUM_RED_PLUS_CARDS) {
                        state.customerConfig.redplusIds =
                            toggleArrayItem(
                                state.customerConfig.redplusIds,
                                action.payload.id,
                                id => id === action.payload.id,
                            );
                    }
                },
            ),
            skipConnectMore: create.reducer<boolean>((state, action) => {
                state.skipConnectMore = action.payload;
            }),
        };
    },
    selectors: {
        selectCustomerConfig: (sliceState): InitialState['customerConfig'] => sliceState.customerConfig,
        selectAvailableSubscriptionIds: (sliceState): InitialState['availableSubscriptionIds'] => sliceState.availableSubscriptionIds,
        selectActiveSubscriptionId: (sliceState): RedTariff | YoungTariff | null =>
            sliceState.customerConfig.selectedSubscriptionId || sliceState.customerConfig.defaultSubscriptionId,
        selectSkipConnectMore: (sliceState): InitialState['skipConnectMore'] => sliceState.skipConnectMore,
        selectPreviousHardwareConfig: (sliceState): InitialState['previousHardwareConfig'] => sliceState.previousHardwareConfig,
        selectHasActiveDevice: (sliceState): boolean => !!sliceState.customerConfig.atomicId || !!sliceState.customerConfig.deviceId,
        selectHasDevice: createSelector(
            [
                (sliceState): InitialState['previousHardwareConfig'] => appSlice.getSelectors().selectPreviousHardwareConfig(sliceState),
                (sliceState): boolean => appSlice.getSelectors().selectHasActiveDevice(sliceState),
            ],
            (previousHardwareConfig, hasDevice): boolean => hasDevice || !!previousHardwareConfig,
        ),
        selectIsEligibleForRestlaufzeit: createSelector(
            [
                (sliceState): InitialState['customerConfig'] => appSlice.getSelectors().selectCustomerConfig(sliceState),
                (sliceState): RedTariff | YoungTariff | null => appSlice.getSelectors().selectActiveSubscriptionId(sliceState),
            ],
            (customerConfig, activeSubscriptionId) =>
                customerConfig.salesChannel !== SALESCHANNEL_YOUNG
                && activeSubscriptionId !== RED_XL_VIRTUAL_ID
                && customerConfig.hasGigakombi,
        ),
        selectAtomicId: createSelector(
            [
                (sliceState): InitialState['customerConfig'] => appSlice.getSelectors().selectCustomerConfig(sliceState),
                (sliceState): InitialState['previousHardwareConfig'] => appSlice.getSelectors().selectPreviousHardwareConfig(sliceState),
            ],
            ({ atomicId }, previousHardwareConfig) => atomicId ?? previousHardwareConfig?.atomicId ?? null,
        ),
    },
});

export default appSlice;

export const {
    setInitialState,
    getData,
    setSubscription,
    setGigakombi,
    setSalesChannel,
    setRestlaufzeit,
    setCashback,
    toggleRedPlusId,
    toggleDevice,
    skipConnectMore,
    goToBasket,
} = appSlice.actions;
export const {
    selectCustomerConfig,
    selectAvailableSubscriptionIds,
    selectActiveSubscriptionId,
    selectSkipConnectMore,
    selectPreviousHardwareConfig,
    selectHasActiveDevice,
    selectIsEligibleForRestlaufzeit,
    selectHasDevice,
    selectAtomicId,
} = appSlice.selectors;
